import { get, post } from "./request";
import { buildApiUrl, API_CONFIG } from "./config";
import type {
  ApiResponse,
  ThreadDetail,
  CreateThreadParams,
  ThreadDetailParams,
} from "./types";

// 帖子相关API
export const threadApi = {
  /**
   * 获取帖子详情（评论）
   * @param tid - 帖子ID
   * @param language - 语言
   * @param order_type - 排序类型，默认0
   * @param author_only - 只显示楼主回复，默认0
   * @param page - 页码，默认1
   * @param page_size - 每页数量，默认10
   * @returns Promise<ApiResponse<ThreadDetail>>
   */
  getDetail: (
    tid: number,
    language: string,
    order_type: number = 0,
    author_only: number = 0,
    page: number = 1,
    page_size: number = 10,
    game_id: number
  ): Promise<ApiResponse<ThreadDetail>> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.THREAD,
      API_CONFIG.ACTIONS.DETAIL,
      {
        tid,
        target_language: language,
        order_type,
        author_only,
        page,
        page_size,
        game_id,
      }
    );
    return get<ApiResponse<ThreadDetail>>(url);
  },

  /**
   * 发布帖子
   * @param fid - 论坛分区ID
   * @param tag_id - 标签ID（可以是单个ID或逗号分隔的多个ID）
   * @param subject - 帖子标题
   * @param message - 帖子内容
   * @param game_id - 游戏ID
   * @param language - 语言
   * @returns Promise<ApiResponse>
   */
  create: (
    fid: number,
    tag_id: number | string,
    subject: string,
    message: string,
    game_id: number,
    language: string
  ): Promise<ApiResponse> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.THREAD,
      API_CONFIG.ACTIONS.CREATE
    );
    const formData = new FormData();
    formData.append("fid", String(fid));
    if (tag_id) {
      formData.append("tag_id", String(tag_id));
    }
    formData.append("subject", subject);
    formData.append("message", message);
    formData.append("game_id", String(game_id));
    formData.append("language", language);
    return post<ApiResponse>(url, formData);
  },

  /**
   * 获取用户帖子列表
   * @param uid - 用户ID
   * @param language - 语言
   * @param page - 页码，默认1
   * @param page_size - 每页数量，默认10
   * @returns Promise<ApiResponse<ThreadDetail[]>>
   */
  getUserThreads: (
    uid: number | string,
    language: string,
    page: number = 1,
    page_size: number = 10,
    game_id: number
  ): Promise<ApiResponse<ThreadDetail[]>> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.THREAD,
      API_CONFIG.ACTIONS.USER_THREADS,
      {
        uid,
        language,
        page,
        page_size,
        game_id,
      }
    );
    return get<ApiResponse<ThreadDetail[]>>(url);
  },
  /**
   * 删除帖子
   * @param tid - 帖子ID
   * @param game_id - 游戏ID
   * @returns Promise<ApiResponse>
   */
  delete: (tid: number, game_id: number): Promise<ApiResponse> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.THREAD,
      API_CONFIG.ACTIONS.DELETE,
      { tid, game_id }
    );
    return post<ApiResponse>(url);
  },

  /**
   * 获取标签信息
   * @param game_id - 游戏ID
   * @param language - 语言
   * @returns Promise<ApiResponse>
   */
  getTagInfo: (game_id: number, language: string): Promise<ApiResponse> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.THREAD,
      API_CONFIG.ACTIONS.GET_TAG_INFO,
      {
        game_id,
        target_language: language,
      }
    );
    return get<ApiResponse>(url);
  },
};
